import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AirIcon from '@mui/icons-material/Air';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import WarningIcon from '@mui/icons-material/Warning';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import { getTileUrl, getTileAttribution } from '../../utils/map-utils';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';

const CONTAGEM_CENTER: [number, number] = [-19.93, -44.05];

const CORES_CONDICOES: Record<string, string> = {
  Hipertensao: '#1E3A5F',
  Diabetes: '#E74C3C',
  DPOC: '#2D7D9A',
  Asma: '#F39C12',
  Obesidade: '#8E44AD',
  'Insuf. Cardiaca': '#E67E22',
  'Insuf. Cardíaca': '#E67E22',
  AVC: '#C0392B',
  IRC: '#16A085',
};

const CORES_RISCO: Record<string, string> = {
  muito_alto: '#E74C3C',
  alto: '#F39C12',
  medio: '#F1C40F',
  baixo: '#27AE60',
};

const LABEL_RISCO: Record<string, string> = {
  muito_alto: 'Muito Alto',
  alto: 'Alto',
  medio: 'Medio',
  baixo: 'Baixo',
};

const PIE_CORES = ['#E74C3C', '#F39C12', '#F1C40F', '#27AE60', '#1E3A5F', '#2D7D9A'];

const toISO = (d: Date): string => d.toISOString().slice(0, 10);
const fmtBr = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const getCorCondicao = (condicao: string, index: number): string =>
  CORES_CONDICOES[condicao] || `hsl(${index * 45}, 60%, 50%)`;

const FitBounds: React.FC<{ points: { latitude: number; longitude: number }[] }> = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [Number(p.latitude), Number(p.longitude)]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [map, points]);
  return null;
};

const MonitoramentoDcnt: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const reportRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [filtroCondicao, setFiltroCondicao] = useState('');
  const [filtroRisco, setFiltroRisco] = useState('');

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  const filtrosParams = useMemo(() => {
    const p: Record<string, string | undefined> = {};
    if (filtroCondicao) p.codigo_dcnt = filtroCondicao;
    if (filtroRisco) p.nivel_risco = filtroRisco;
    return p;
  }, [filtroCondicao, filtroRisco]);

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getDcnt(
          municipioAtivo.id,
          dataInicio,
          dataFim,
          filtrosParams,
        );
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de DCNT.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim, filtrosParams]);

  const { evolucaoPivotada, condicoesEvolucao } = useMemo(() => {
    const evolucao: any[] = data?.evolucao || [];
    const mesMap: Record<string, Record<string, number | string>> = {};
    const condicoesSet = new Set<string>();
    evolucao.forEach((item: any) => {
      const mes = item.mes;
      const condicao = item.condicao;
      condicoesSet.add(condicao);
      if (!mesMap[mes]) mesMap[mes] = { mes };
      mesMap[mes][condicao] = Number(item.total || 0);
    });
    return {
      evolucaoPivotada: Object.values(mesMap).sort((a: any, b: any) =>
        String(a.mes).localeCompare(String(b.mes)),
      ),
      condicoesEvolucao: Array.from(condicoesSet),
    };
  }, [data?.evolucao]);

  const { unidadesPivotadas, condicoesUnidade } = useMemo(() => {
    const porUnidade: any[] = data?.porUnidade || [];
    const unidadeMap: Record<string, Record<string, any>> = {};
    const condicoesSet = new Set<string>();
    porUnidade.forEach((item: any) => {
      const nome = item.nome_unidade;
      const condicao = item.condicao;
      condicoesSet.add(condicao);
      if (!unidadeMap[nome]) unidadeMap[nome] = { nome_unidade: nome };
      unidadeMap[nome][condicao] = (unidadeMap[nome][condicao] || 0) + Number(item.total || 0);
    });
    const condicoes = Array.from(condicoesSet);
    const pivotada = Object.values(unidadeMap)
      .map((row: any) => ({
        ...row,
        total: condicoes.reduce((sum, cond) => sum + (row[cond] || 0), 0),
      }))
      .sort((a: any, b: any) => b.total - a.total);
    return { unidadesPivotadas: pivotada, condicoesUnidade: condicoes };
  }, [data?.porUnidade]);

  const estratificacaoPie = useMemo(() => {
    const rows: any[] = data?.estratificacao || [];
    const map: Record<string, number> = {};
    rows.forEach((r: any) => {
      const nivel = r.nivel_risco || 'baixo';
      map[nivel] = (map[nivel] || 0) + Number(r.total || 0);
    });
    return Object.entries(map).map(([nivel, total]) => ({
      nivel,
      label: LABEL_RISCO[nivel] || nivel,
      total,
    }));
  }, [data?.estratificacao]);

  const sexoAgg = useMemo(() => {
    const rows: any[] = data?.segmentacaoSexo || [];
    const map: Record<string, number> = {};
    rows.forEach((r: any) => {
      map[r.sexo] = (map[r.sexo] || 0) + Number(r.total || 0);
    });
    return Object.entries(map).map(([sexo, total]) => ({ sexo, total }));
  }, [data?.segmentacaoSexo]);

  const faixaAgg = useMemo(() => {
    const rows: any[] = data?.segmentacaoFaixa || [];
    const map: Record<string, number> = {};
    rows.forEach((r: any) => {
      map[r.faixa_etaria] = (map[r.faixa_etaria] || 0) + Number(r.total || 0);
    });
    return Object.entries(map)
      .map(([faixa_etaria, total]) => ({ faixa_etaria, total }))
      .sort((a, b) => a.faixa_etaria.localeCompare(b.faixa_etaria));
  }, [data?.segmentacaoFaixa]);

  const mapaPontos = useMemo(() => {
    const unidades: any[] = (data?.mapaCalor || []).filter(
      (p: any) => p.latitude != null && p.longitude != null,
    );
    const bairros: any[] = (data?.prevalenciaBairro || []).filter(
      (p: any) => p.latitude != null && p.longitude != null,
    );
    return { unidades, bairros };
  }, [data?.mapaCalor, data?.prevalenciaBairro]);

  const maxMapaTotal = useMemo(
    () =>
      Math.max(
        ...mapaPontos.unidades.map((p: any) => Number(p.total || 0)),
        ...mapaPontos.bairros.map((p: any) => Number(p.total || 0)),
        1,
      ),
    [mapaPontos],
  );

  const tratamentoMes = useMemo(() => {
    const rows: any[] = data?.tratamento || [];
    const map: Record<string, Record<string, number | string>> = {};
    rows.forEach((r: any) => {
      if (!map[r.mes]) {
        map[r.mes] = {
          mes: r.mes,
          adesao: 0,
          consultas: 0,
          medicacao: 0,
          n: 0,
        };
      }
      const row = map[r.mes];
      row.adesao = (Number(row.adesao) + Number(r.adesao_percentual || 0)) as number;
      row.consultas = (Number(row.consultas) + Number(r.consultas_realizadas || 0)) as number;
      row.medicacao = (Number(row.medicacao) + Number(r.pacientes_em_medicacao || 0)) as number;
      row.n = (Number(row.n) + 1) as number;
    });
    return Object.values(map)
      .map((r: any) => ({
        mes: r.mes,
        adesao_percentual: r.n ? Math.round((r.adesao / r.n) * 10) / 10 : 0,
        consultas_realizadas: r.consultas,
        pacientes_em_medicacao: r.medicacao,
      }))
      .sort((a: any, b: any) => String(a.mes).localeCompare(String(b.mes)));
  }, [data?.tratamento]);

  const opcoesCondicao: { codigo: string; nome: string }[] = useMemo(() => {
    const rows = data?.filtrosOpcoes || [];
    return rows.map((r: any) => ({ codigo: r.codigo_dcnt, nome: r.nome_dcnt }));
  }, [data?.filtrosOpcoes]);

  const handleExportCsv = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    const params: Record<string, string> = {
      data_inicio: dataInicio,
      data_fim: dataFim,
    };
    if (filtroCondicao) params.codigo_dcnt = filtroCondicao;
    if (filtroRisco) params.nivel_risco = filtroRisco;
    const path = monitoramentoService.getDcntCsvUrl(municipioAtivo.id, params);
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:3001`;
    const res = await fetch(`${base}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dcnt-relatorio-${dataFim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [municipioAtivo?.id, dataInicio, dataFim, filtroCondicao, filtroRisco]);

  const handleExportPdf = () => {
    window.print();
  };

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de DCNT.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = data?.kpis || {};
  const desfechos = data?.desfechos || {};
  const alertas: any[] = data?.alertas || [];

  const totalAltoRisco = estratificacaoPie
    .filter((e) => e.nivel === 'alto' || e.nivel === 'muito_alto')
    .reduce((s, e) => s + e.total, 0);

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }} ref={reportRef} className="dcnt-report">
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            DCNT - Doenças Crônicas Não Transmissíveis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {municipioAtivo.nome}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Periodo: {fmtBr(dataInicio)} a {fmtBr(dataFim)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
          <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={handleDateChange} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Condicao</InputLabel>
              <Select value={filtroCondicao} label="Condicao" onChange={(e) => setFiltroCondicao(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {opcoesCondicao.map((o) => (
                  <MenuItem key={o.codigo} value={o.codigo}>{o.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Risco</InputLabel>
              <Select value={filtroRisco} label="Risco" onChange={(e) => setFiltroRisco(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="muito_alto">Muito Alto</MenuItem>
                <MenuItem value="alto">Alto</MenuItem>
                <MenuItem value="medio">Medio</MenuItem>
                <MenuItem value="baixo">Baixo</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {alertas.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, borderLeft: '4px solid', borderColor: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningIcon color="error" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>Vigilancia — alertas ativos</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {alertas.slice(0, 8).map((a: any, i: number) => (
              <Chip
                key={i}
                size="small"
                label={`${a.nome_dcnt}: +${a.variacao_percentual}%`}
                color="error"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Visao Geral" />
        <Tab label="Estratificacao (11.1)" />
        <Tab label="Territorial (11.2)" />
        <Tab label="Tratamento (11.3)" />
        <Tab label="Desfechos (11.4)" />
        <Tab label="Relatorios (11.5)" />
      </Tabs>

      {tab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard titulo="Diabetes Mellitus" valor={Number(kpis.total_diabetes || 0).toLocaleString('pt-BR')} icone={<BloodtypeIcon />} cor="#E74C3C" subtitulo="Pacientes cadastrados" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard titulo="Hipertensao" valor={Number(kpis.total_hipertensao || 0).toLocaleString('pt-BR')} icone={<MonitorHeartIcon />} cor="#1E3A5F" subtitulo="Pacientes cadastrados" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard titulo="DPOC" valor={Number(kpis.total_dpoc || 0).toLocaleString('pt-BR')} icone={<AirIcon />} cor="#2D7D9A" subtitulo="Pacientes cadastrados" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard titulo="Alto / Muito Alto Risco" valor={totalAltoRisco.toLocaleString('pt-BR')} icone={<WarningIcon />} cor="#E74C3C" subtitulo="Estratificacao atual" />
            </Grid>
          </Grid>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Registros por Condicao (Empilhado por Mes)</Typography>
            {evolucaoPivotada.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={evolucaoPivotada}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" />
                  <YAxis tickFormatter={(v) => Number(v).toLocaleString('pt-BR')} />
                  <Tooltip formatter={(v: any, name: any) => [Number(v).toLocaleString('pt-BR'), name]} />
                  <Legend />
                  {condicoesEvolucao.map((cond, index) => (
                    <Bar key={cond} dataKey={cond} stackId="evolucao" fill={getCorCondicao(cond, index)} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados de evolucao.</Typography>
            )}
          </Paper>

          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Pacientes DCNT por Unidade</Typography>
            {unidadesPivotadas.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                      {condicoesUnidade.map((cond) => (
                        <TableCell key={cond} align="right" sx={{ fontWeight: 600 }}>{cond}</TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unidadesPivotadas.slice(0, 20).map((row: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{row.nome_unidade}</TableCell>
                        {condicoesUnidade.map((cond) => (
                          <TableCell key={cond} align="right">{(row[cond] || 0).toLocaleString('pt-BR')}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{(row.total || 0).toLocaleString('pt-BR')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados por unidade.</Typography>
            )}
          </Paper>
        </>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Distribuicao por Nivel de Risco</Typography>
              {estratificacaoPie.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={estratificacaoPie} dataKey="total" nameKey="label" cx="50%" cy="50%" outerRadius={100} label>
                      {estratificacaoPie.map((entry, i) => (
                        <Cell key={entry.nivel} fill={CORES_RISCO[entry.nivel] || PIE_CORES[i % PIE_CORES.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => [Number(v).toLocaleString('pt-BR'), 'Pacientes']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados de estratificacao.</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Pacientes por Condicao e Risco</Typography>
              <Box sx={{ overflowX: 'auto', maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Nivel</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Condicao</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.estratificacao || []).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip size="small" label={LABEL_RISCO[r.nivel_risco] || r.nivel_risco} sx={{ bgcolor: CORES_RISCO[r.nivel_risco], color: '#fff' }} />
                        </TableCell>
                        <TableCell>{r.condicao}</TableCell>
                        <TableCell align="right">{Number(r.total).toLocaleString('pt-BR')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 2 && (
        <>
          <Paper elevation={1} sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2, pb: 0 }}>Mapa de Calor — Prevalencia por Unidade e Bairro</Typography>
            {mapaPontos.unidades.length > 0 || mapaPontos.bairros.length > 0 ? (
              <MapContainer center={CONTAGEM_CENTER} zoom={12} scrollWheelZoom style={{ height: 420, width: '100%' }}>
                <TileLayer attribution={getTileAttribution()} url={getTileUrl()} />
                <FitBounds points={[...mapaPontos.unidades, ...mapaPontos.bairros]} />
                {mapaPontos.unidades.map((p: any, idx: number) => {
                  const t = Number(p.total || 0);
                  const intensity = t / maxMapaTotal;
                  return (
                    <CircleMarker
                      key={`u-${idx}`}
                      center={[Number(p.latitude), Number(p.longitude)]}
                      radius={8 + intensity * 18}
                      pathOptions={{ color: '#1E3A5F', fillColor: '#2D7D9A', fillOpacity: 0.55 }}
                    >
                      <Popup>
                        <strong>{p.nome_unidade}</strong>
                        <br />Casos DCNT: {t.toLocaleString('pt-BR')}
                      </Popup>
                    </CircleMarker>
                  );
                })}
                {mapaPontos.bairros.map((p: any, idx: number) => {
                  const t = Number(p.total || 0);
                  const intensity = t / maxMapaTotal;
                  return (
                    <CircleMarker
                      key={`b-${idx}`}
                      center={[Number(p.latitude), Number(p.longitude)]}
                      radius={6 + intensity * 14}
                      pathOptions={{ color: '#8E44AD', fillColor: '#E74C3C', fillOpacity: 0.45 }}
                    >
                      <Popup>
                        <strong>{p.nome_bairro}</strong>
                        <br />Prevalencia: {t.toLocaleString('pt-BR')}
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>Sem coordenadas para mapa.</Typography>
            )}
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Segmentacao por Sexo</Typography>
                {sexoAgg.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={sexoAgg}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="sexo" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="total" fill="#1E3A5F" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Sem dados</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Segmentacao por Faixa Etaria</Typography>
                {faixaAgg.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={faixaAgg}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="faixa_etaria" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="total" fill="#2D7D9A" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Sem dados</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Prevalencia por Bairro</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Bairro</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Condicoes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.prevalenciaBairro || []).slice(0, 15).map((r: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{r.nome_bairro}</TableCell>
                        <TableCell align="right">{Number(r.total).toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">{r.condicoes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {tab === 3 && (
        <>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Evolucao do Tratamento — Adesao e Consultas</Typography>
            {tratamentoMes.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={tratamentoMes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="adesao_percentual" name="Adesao %" stroke="#27AE60" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="consultas_realizadas" name="Consultas" stroke="#1E3A5F" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="pacientes_em_medicacao" name="Em medicacao" stroke="#E74C3C" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados de tratamento.</Typography>
            )}
          </Paper>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Desempenho por Unidade</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Adesao media %</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Consultas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Em medicacao</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.tratamentoUnidade || []).map((r: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell>{r.nome_unidade}</TableCell>
                    <TableCell align="right">{r.adesao_media}%</TableCell>
                    <TableCell align="right">{Number(r.consultas).toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">{Number(r.em_medicacao).toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {tab === 4 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <KpiCard titulo="Internacoes DCNT" valor={Number(desfechos.total_internacoes || 0).toLocaleString('pt-BR')} icone={<FavoriteIcon />} cor="#1E3A5F" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard titulo="Permanencia Media" valor={`${desfechos.permanencia_media || 0} dias`} icone={<MonitorHeartIcon />} cor="#2D7D9A" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard
                titulo="Custo Evitavel"
                valor={`R$ ${Number(desfechos.custo_evitavel || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
                icone={<WarningIcon />}
                cor="#E74C3C"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Internacoes por CID</Typography>
                {(data?.desfechosCid || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.desfechosCid} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="descricao_cid" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="internacoes" fill="#E74C3C" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem desfechos.</Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Evolucao Mensal</Typography>
                {(data?.desfechosEvolucao || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.desfechosEvolucao}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="mes" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="internacoes" name="Internacoes" stroke="#1E3A5F" />
                      <Line type="monotone" dataKey="custo_evitavel" name="Custo evitavel" stroke="#E74C3C" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem evolucao.</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {tab === 5 && (
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Relatorios Exportaveis</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Exporte o consolidado de estratificacao, demografia, tratamento e desfechos para o periodo e filtros selecionados.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
              Exportar Excel (CSV)
            </Button>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
              Exportar PDF (impressao)
            </Button>
          </Box>
          <Alert severity="info" sx={{ mt: 3 }}>
            O relatorio CSV inclui todas as secoes do painel DCNT. O PDF utiliza a impressao do navegador sobre o conteudo atual do painel.
          </Alert>
        </Paper>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="dcnt"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoDcnt;
